import { BUSINESS } from "@/lib/config";
import { Phone, Clock, CheckCircle2, Zap, ShieldCheck } from "lucide-react";

const benefits = [
  {
    icon: Zap,
    title: "Same-week appointments",
    text: "Most jobs scheduled within 2–5 days. Emergency? We'll do our best to get you in sooner.",
  },
  {
    icon: ShieldCheck,
    title: "Honest, upfront pricing",
    text: "Your quote is your final price. No surprise add-ons, no upsells, no nickel-and-diming.",
  },
  {
    icon: CheckCircle2,
    title: "Real human, every call",
    text: "When you call, you talk to a real BID team member — never a bot or a sales script.",
  },
];

export default function BookingEmbed() {
  return (
    <section
      id="book"
      data-testid="booking-embed-section"
      className="bg-slate-50 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Heading */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="bid-divider" />
              <span className="bid-eyebrow">Schedule Your Service</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight">
              Ready to book?
              <br />
              Give us a quick call.
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-base text-slate-700 leading-relaxed">
              Tap the number below — we&apos;ll pick up live during business
              hours and lock in your appointment in under 5 minutes. Outside
              hours, leave a voicemail and we&apos;ll call back first thing in
              the morning.
            </p>
          </div>
        </div>

        {/* Phone CTA card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 bid-noise border-2 border-slate-900">
          {/* Left — primary phone CTA */}
          <div className="lg:col-span-7 bg-slate-900 text-white p-10 md:p-14 relative overflow-hidden">
            <div className="bid-grid-bg absolute inset-0 opacity-10" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-9 h-9 bg-[#FF2A2A] text-white flex items-center justify-center">
                  <Phone size={18} strokeWidth={2.5} />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-slate-400">
                  Call to Book · St. Augustine
                </div>
              </div>

              <h3 className="font-display font-black text-3xl md:text-4xl uppercase leading-[0.95] tracking-tight mb-3 text-slate-300">
                Tap the number.
              </h3>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                data-testid="book-primary-call"
                className="block font-display font-black text-5xl md:text-7xl uppercase leading-none tracking-tighter mb-8 hover:text-[#FF2A2A] transition-colors"
              >
                {BUSINESS.phone}
              </a>

              <p className="text-slate-300 max-w-md leading-relaxed mb-10">
                One quick call locks in your time slot. We&apos;ll confirm your
                home type, roof type, and zip code — and give you your exact
                final price before we hang up.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  data-testid="book-call-button"
                  className="inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#cc2222] transition-all text-white font-black uppercase tracking-widest text-sm px-8 py-5 border-2 border-[#FF2A2A] hover:-translate-y-0.5"
                >
                  <Phone size={18} />
                  Call {BUSINESS.phone}
                </a>
                <a
                  href={`mailto:${BUSINESS.email}?subject=Dryer Vent Cleaning - Booking Request`}
                  data-testid="book-email-button"
                  className="inline-flex items-center gap-3 border-2 border-white/40 hover:border-white hover:bg-white hover:text-slate-900 transition-all text-white font-bold uppercase tracking-widest text-sm px-8 py-5"
                >
                  Or email us
                </a>
              </div>

              {/* Trust row */}
              <div className="flex flex-wrap items-center gap-6 pt-8 border-t border-slate-700">
                <TrustItem label="Avg call length" value="3 min" />
                <TrustItem label="Booked within" value="2–5 days" />
                <TrustItem label="Live answer rate" value="98%" />
              </div>
            </div>
          </div>

          {/* Right — hours panel */}
          <div className="lg:col-span-5 bg-white p-10 md:p-12 border-l-0 lg:border-l-2 border-slate-900 border-t-2 lg:border-t-0">
            <div className="flex items-center gap-2 mb-6">
              <Clock size={18} className="text-[#002FA7]" />
              <div className="bid-eyebrow">Phone Hours</div>
            </div>
            <ul className="space-y-2 mb-8">
              {BUSINESS.hoursDetailed.map((d) => {
                const isClosed = d.hours === "Closed";
                return (
                  <li
                    key={d.day}
                    className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0"
                  >
                    <span className="font-display font-bold uppercase text-sm tracking-tight text-slate-900">
                      {d.day}
                    </span>
                    <span
                      className={`font-mono text-sm ${
                        isClosed
                          ? "text-[#FF2A2A] font-bold"
                          : "text-slate-700"
                      }`}
                    >
                      {d.hours}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="bg-slate-50 border-l-4 border-[#002FA7] p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-[#002FA7] mb-1">
                Service area
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {BUSINESS.address}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {BUSINESS.googleCategory}
              </div>
            </div>
          </div>
        </div>

        {/* Benefits row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-x-2 border-b-2 border-slate-900">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={b.title}
                data-testid={`book-benefit-${i}`}
                className={`bg-white p-6 md:p-8 ${
                  i !== benefits.length - 1
                    ? "border-b md:border-b-0 md:border-r border-slate-200"
                    : ""
                }`}
              >
                <Icon size={22} className="text-[#002FA7] mb-3" />
                <div className="font-display font-bold uppercase tracking-tight text-base mb-1.5">
                  {b.title}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {b.text}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TrustItem({ label, value }) {
  return (
    <div>
      <div className="font-display font-black text-2xl">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-0.5">
        {label}
      </div>
    </div>
  );
}
